<template>
  <DeleteConfirmDialog
    v-model:visible="disconnectDialogVisibility"
    :loading="deleteLoading"
    :title="i18n.t('disconnectDataSource')"
    :sub-title="
      i18n.t('disconnectDataSourceDescription', {
        name: dataSource.name,
      })
    "
    @cancel="disconnectDialogVisibility = false"
    @delete="disconnectDatasource" />

  <DataSourceDrawer
    v-model:visibility="dataSourceDrawerVisibility"
    :data-source="dataSource"
    :is-name-field-disabled="false"
    @refetch-data-source="emit('refetchDataSource')" />

  <OptimizationDrawer
    v-model:drawer-visibility="optimizationsDrawerVisibility"
    :filter="{
      application_ids: [dataSource?._id],
      hidden: false,
      deprecated: false,
    }" />

  <el-card
    shadow="always"
    :body-style="{
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
      justifyContent: 'space-between',
      padding: '20px 40px',
      alignItems: 'center',
    }">
    <div class="stats-card-application-info-container">
      <el-image
        v-if="dataSource.integration_id"
        class="company-img"
        :src="`https://logos.saasmetrix.io/${encodeURIComponent(dataSource?.integration_id)}.png`"
        fit="contain">
        <template #error>
          <ImageOff :size="30" />
        </template>
      </el-image>
      <ImageOff v-else :size="30" />
      <div class="text-wrapper">
        <div class="text-row">
          <TextMultiline
            :text="dataSource?.name || ''"
            class="text-xl font-semibold"
            tooltip-max-width="160px" />

          <SmDropdown type="panel" placement="right">
            <template #trigger>
              <TextMultiline :show-tool-tip="false">
                <div class="item-center group/button flex w-full gap-1">
                  <SquarePen
                    :size="16"
                    class="mb-[2px] stroke-contrast-muted hover:stroke-contrast"
                    style="cursor: pointer" />
                </div>
              </TextMultiline>
            </template>
            <SmDropdownItem @click="handleApplicationEditButton">
              {{ i18n.t('editDataSource') }}
            </SmDropdownItem>
            <SmDropdownItem @click="disconnectDialogVisibility = true">
              {{ i18n.t('disconnectDataSource') }}
            </SmDropdownItem>
            <SmDropdownItem @click="emit('syncGroups')">
              {{ i18n.t('syncGroups') }}
            </SmDropdownItem>
          </SmDropdown>
        </div>
        <TextMultiline>
          {{
            i18n.t('views.externalDataSources.lastUpdated', {
              time: dataSource?.last_updated
                ? displayDate(new Date(dataSource?.last_updated), {
                    dateStyle: 'long',
                    timeStyle: 'short',
                  })
                : '-',
            })
          }}
        </TextMultiline>
      </div>
    </div>
    <StatsElement
      :display-number="optimizationCount.toString()"
      display-i18n-key="detectedOptimizations"
      :icon="ChartLine"
      :clickable="true"
      color="purple"
      @click="handleShowOptimizations" />
    <StatsElement
      :display-number="employeeCount.toString()"
      display-i18n-key="employees"
      :icon="User"
      color="red" />
  </el-card>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import { EmployeesService, DataSource, DataSourcesService } from '@/client'
  import { displayDate } from '@/utils/formatter/date'
  import { OptimizationStore } from '@/stores/optimization/optimization.store'
  import { SquarePen, User, ChartLine, ImageOff } from 'lucide-vue-next'
  import router from '@/router'
  const i18n = useI18n()

  const props = defineProps<{ dataSource: DataSource }>()
  const emit = defineEmits<{ refetchDataSource: []; syncGroups: [] }>()

  const dataSourceDrawerVisibility = ref<boolean>(false)
  const optimizationsDrawerVisibility = ref<boolean>(false)

  const handleShowOptimizations = () => {
    optimizationsDrawerVisibility.value = true
  }

  const handleApplicationEditButton = () => {
    dataSourceDrawerVisibility.value = true
  }

  const employeeCount = ref<number>(0)
  const optimizationCount = ref<number>(0)

  watch(
    () => props.dataSource,
    () => {
      if (props.dataSource._id) {
        getEmployeeCount()
        getOptimizationCount()
      }
    },
    { immediate: true }
  )

  function getOptimizationCount() {
    OptimizationStore.getOptimizationsCount({
      application_ids: [props.dataSource._id],
      hidden: false,
      deprecated: false,
    }).then((res) => {
      optimizationCount.value = res
    }, console.error)
  }

  function getEmployeeCount() {
    EmployeesService.countDataSourceEmployeesApiV1DataSourceDataSourcesDataSourceIdEmployeesCountGet(
      {
        dataSourceId: props.dataSource._id,
      }
    ).then((res) => {
      employeeCount.value = res
    })
  }

  const deleteLoading = ref(false)
  const disconnectDialogVisibility = ref(false)

  function disconnectDatasource() {
    DataSourcesService.deleteDataSourceApiV1DataSourceDataSourcesDataSourceIdDelete(
      {
        dataSourceId: props.dataSource._id,
      }
    ).then(() => {
      disconnectDialogVisibility.value = false
      router.push({
        name: 'Data-Sources',
      })
    })
  }

  defineExpose({
    employeeCount,
    optimizationCount,
  })
</script>

<style lang="scss" scoped>
  .stats-card-application-info-container {
    display: grid;
    place-content: center;
    grid-template-columns: 50px 1fr;
    gap: 20px;
    width: 32%;
    height: 100%;

    .company-img {
      height: 100%;
      width: 100%;
    }

    .text-wrapper {
      display: flex;
      flex-direction: column;

      .text-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
      }
    }
  }
</style>
